import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { clientApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ClientLayout } from "@/components/layout/ClientLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { formatCurrency, formatDate } from "@/lib/format";
import { TrendingUp, Wallet, Repeat, RefreshCw, Search, ArrowRight, AlertTriangle } from "lucide-react";

const QUICK_ACTIONS = [
  { to: "/funds", label: "Purchase", icon: Search, desc: "Browse & invest in real BSE schemes" },
  { to: "/sips", label: "Systematic Plans", icon: Repeat, desc: "SIP, XSIP, STP & SWP" },
  { to: "/orders?type=redeem", label: "Redeem", icon: Wallet, desc: "Withdraw from your holdings" },
  { to: "/switch", label: "Switch", icon: RefreshCw, desc: "Move between schemes" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, o] = await Promise.all([
          clientApi.get("/portfolio/summary"),
          clientApi.get("/orders"),
        ]);
        setSummary(s.data);
        setOrders(o.data.orders.slice(0, 5));
      } finally {
        setLoading(false);
      }
    })();
    clientApi.get("/notifications").then((r) => setNotes(r.data)).catch(() => {});
  }, []);

  const kycApproved = user?.kyc_status === "APPROVED";

  return (
    <ClientLayout>
      <div className="mb-6">
        <h1 className="font-heading text-3xl text-[var(--brand)]">Welcome, {user?.full_name?.split(" ")[0]}</h1>
        <p className="mt-1 text-sm text-[var(--ink-muted)]">Here&apos;s an overview of your mutual fund portfolio.</p>
      </div>

      {!kycApproved && (
        <Card className="mb-6 border-amber-200 bg-amber-50">
          <CardContent className="flex flex-col items-start gap-4 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <div>
                <div className="font-medium text-amber-900">
                  {user?.kyc_status === "PENDING_REVIEW" ? "Your KYC is pending SMIFS Ops review." : "Complete your KYC to start investing."}
                </div>
                <div className="text-sm text-amber-700">You&apos;ll be able to purchase, SIP, switch and redeem once approved.</div>
              </div>
            </div>
            {user?.kyc_status !== "PENDING_REVIEW" && (
              <Button asChild size="sm" className="w-full rounded-full bg-amber-600 hover:bg-amber-700 sm:w-auto" data-testid="dashboard-complete-kyc-button">
                <Link to="/onboarding">Complete KYC</Link>
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="glass-card border-0">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 text-sm text-[var(--ink-muted)]"><Wallet className="h-4 w-4" /> Current Value</div>
            <div className="mt-2 font-heading text-2xl text-[var(--brand)]" data-testid="summary-current-value">{formatCurrency(summary?.summary?.current_value)}</div>
            <div className={`mt-1 text-xs ${(summary?.summary?.day_change ?? 0) >= 0 ? "text-emerald-700" : "text-red-600"}`}>
              Day: {(summary?.summary?.day_change ?? 0) >= 0 ? "+" : ""}{formatCurrency(summary?.summary?.day_change)}
            </div>
          </CardContent>
        </Card>
        <Card className="glass-card border-0">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 text-sm text-[var(--ink-muted)]"><TrendingUp className="h-4 w-4" /> Invested</div>
            <div className="mt-2 font-heading text-2xl text-[var(--brand)]" data-testid="summary-invested-amount">{formatCurrency(summary?.summary?.invested)}</div>
            <div className="mt-1 text-xs text-[var(--ink-muted)]">{summary?.summary?.schemes_count ?? 0} schemes</div>
          </CardContent>
        </Card>
        <Card className="glass-card border-0">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 text-sm text-[var(--ink-muted)]"><TrendingUp className="h-4 w-4" /> Overall Gain</div>
            <div className={`mt-2 font-heading text-2xl ${(summary?.summary?.gain ?? 0) >= 0 ? "text-emerald-700" : "text-red-600"}`} data-testid="summary-gain">
              {(summary?.summary?.gain ?? 0) >= 0 ? "+" : ""}{formatCurrency(summary?.summary?.gain)}
            </div>
            <div className="mt-1 text-xs text-[var(--ink-muted)]">{summary?.summary?.gain_pct != null ? `${summary.summary.gain_pct}% · XIRR ${summary?.summary?.active_xirr ?? "—"}%` : ""}</div>
          </CardContent>
        </Card>
        <Card className="glass-card border-0">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 text-sm text-[var(--ink-muted)]"><Repeat className="h-4 w-4" /> Active SIPs</div>
            <div className="mt-2 font-heading text-2xl text-[var(--brand)]">{(summary?.active_sips ?? 0) + (summary?.active_xsips ?? 0)}</div>
            <div className="mt-1 text-xs text-[var(--ink-muted)]">{summary?.active_xsips ?? 0} on e-NACH mandate</div>
          </CardContent>
        </Card>
      </div>
      <div className="-mt-4 mb-8">
        <Link to="/portfolio" className="text-sm text-[var(--cta)] hover:underline" data-testid="dashboard-view-portfolio-link">View full portfolio & capital gains →</Link>
      </div>
      {notes.length > 0 && (
        <div className="mb-8 space-y-2" data-testid="dashboard-notifications">
          {notes.slice(0, 4).map((n, i) => (
            <div key={i} className={`flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm ${n.severity === "warning" ? "border-amber-200 bg-amber-50 text-amber-800" : "border-[var(--glass-border)] bg-[var(--surface-2)] text-[var(--ink)]"}`}>
              <AlertTriangle className="h-4 w-4 shrink-0" /> {n.message}
            </div>
          ))}
        </div>
      )}

      <h2 className="mb-3 font-heading text-lg text-[var(--brand)]">Quick actions</h2>
      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {QUICK_ACTIONS.map((a) => (
          <Link key={a.label} to={a.to} data-testid={`quick-action-${a.label.toLowerCase()}`}>
            <Card className="h-full border-[var(--glass-border)] transition-shadow hover:shadow-md">
              <CardContent className="flex flex-col gap-2 p-5">
                <a.icon className="h-5 w-5 text-[var(--cta)]" />
                <div className="font-medium text-[var(--brand)]">{a.label}</div>
                <div className="text-xs text-[var(--ink-muted)]">{a.desc}</div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <h2 className="font-heading text-lg text-[var(--brand)]">Recent orders</h2>
        <Link to="/orders" className="flex items-center gap-1 text-sm text-[var(--cta)]" data-testid="view-all-orders-link">View all <ArrowRight className="h-3.5 w-3.5" /></Link>
      </div>
      <Card className="mt-3 border-[var(--glass-border)]">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-sm text-[var(--ink-muted)]">Loading...</div>
          ) : orders.length === 0 ? (
            <div className="p-6 text-sm text-[var(--ink-muted)]" data-testid="no-orders-message">No orders yet. Explore funds to get started.</div>
          ) : (
            <div className="divide-y divide-[var(--glass-border)]">
              {orders.map((o) => (
                <div key={o.order_number} className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`order-row-${o.order_number}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-[var(--ink)]">{o.scheme_name}</div>
                    <div className="text-xs text-[var(--ink-muted)]">{o.order_type} &middot; {formatDate(o.created_at)} &middot; Order #{o.order_number}</div>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="text-right">
                      <div className="font-medium">{o.amount ? formatCurrency(o.amount) : `${o.qty} units`}</div>
                    </div>
                    <StatusBadge status={o.status} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </ClientLayout>
  );
}
